/* src/Pages/SlotsInactivePage/index.tsx */
import React, { useEffect, useState, useCallback, useMemo, useRef, startTransition } from 'react';
import { Card, Space, message, Spin } from 'antd';
import type { Key } from 'react';

import {
  fetchSlots,
  fetchAllSlots,
  deleteSlot,
} from '../../Modules/Slot/Presentation/actions';

import { Slot, SlotSearchParams } from '../../Modules/Slot/Model/Slot';
import SlotTable from './components/SlotTable';
import ManagementBar from './components/ManagementBar';
import SlotOpsOverrideBanner from '../SlotsPage/components/SlotOpsOverrideBanner';

import { useAppDispatch, useAppSelector } from '../../Modules/Presentation/hooks';
import { useSlots } from '../../Modules/Slot/Presentation/hooks/hooks';
import { selectUser } from '../../Modules/User/Presentation/selectors';
import { selectAccounts } from '../../Modules/Account/Presentation/selectors';
import { fetchAccounts } from '../../Modules/Account/Presentation/actions';
import useOnce from '../../Utils/hook/useOnce';
import { getAccountIdById } from '../../Utils/getAccountNameById';
import { assertAdminOrManager } from '../../Modules/Slot/Domain/UseCase/Utils/Permission';
import {
  optimisticDeleteSlots,
  rollbackSlots,
} from '../../Modules/Slot/Presentation/reducers';

const SlotsInactivePage: React.FC = () => {

  /** ---------------------------------------------------------------- */
  const dispatch = useAppDispatch();
  const me = useAppSelector(selectUser);
  const accounts = useAppSelector(selectAccounts);
  
  // 컴포넌트 최상단 ref
  const topRef = useRef<HTMLDivElement>(null);

  /* ==============  검색/필터 상태 ============== */
  const [searchParams, setSearchParams] = useState<SlotSearchParams>({
    page: 1,
    pageSize: 20,
    active: 'false' // 비활성 슬롯만 조회
  });

  const [search, setSearch] = useState('');
  const [searchField, setSearchField] = useState<'slot' | 'keyword' | 'url' | 'user' | 'mid_single' | 'mid_compare'>('slot');

  // 검색 유형만 바꿀 때는 목록을 다시 불러오지 않음
  const handleSearchFieldChange = useCallback((field: 'slot' | 'keyword' | 'url' | 'user' | 'mid_single' | 'mid_compare') => {
    setSearchField(field);
    setSearch('');
  }, []);

  /* ==============  UI 상태 ============== */
  const [selected, setSelected] = useState<Key[]>([]);

  /** me 가 바뀔 때마다 슬롯 훅 */
  const { slots, loading, error, pagination, filters, refresh } = useSlots(me!, searchParams);

  // 환불-종료 슬롯 페이지에서는 캐싱 없이 항상 새로 요청
  const fetchInactiveSlots = useCallback(() => {
    if (me) {
      dispatch(fetchSlots({ me, params: searchParams }));
    }
  }, [dispatch, me, searchParams]);

  /* ➜ 최초 한 번: 계정 목록, 슬롯 목록 */
  useOnce(() => { if (accounts.length === 0) dispatch(fetchAccounts()); });
  useOnce(() => { fetchInactiveSlots(); });

  // searchParams가 변경될 때마다 새로 요청
  useEffect(() => {
    if (me) {
      fetchInactiveSlots();
    }
  }, [fetchInactiveSlots, me]);

  // 검색 실행 (필터 종류 변경만으로는 요청하지 않음; 빈 칸 검색 시 1페이지 전체)
  const handleSearch = useCallback((
    value: string,
    _event?:
      | React.ChangeEvent<HTMLInputElement>
      | React.MouseEvent<HTMLElement>
      | React.KeyboardEvent<HTMLInputElement>,
    info?: { source?: 'clear' | 'input' },
  ) => {
    if (!value.trim() && info?.source === 'clear') {
      return;
    }

    if (!value.trim()) {
      setSearchParams(prev => ({
        page: 1,
        pageSize: prev.pageSize,
        active: 'false',
      }));
      return;
    }

    const params: Partial<SlotSearchParams> = {};

    switch (searchField) {
      case 'slot': {
        const slotId = parseInt(value.trim());
        if (!isNaN(slotId)) {
          params.slotId = slotId;
        }
        break;
      }
      case 'keyword':
        params.keyword = value.trim();
        break;
      case 'url':
        params.url = value.trim();
        break;
      case 'user': {
        const query = value.trim().toLowerCase();
        const sellers = accounts.filter(acc => acc.grade === 'SELLER');

        const exactUsername = sellers.find(acc => acc.username.toLowerCase() === query);
        if (exactUsername) {
          params.sellerId = exactUsername.id;
          break;
        }

        const sellerId = parseInt(value.trim());
        if (!isNaN(sellerId) && /^\d+$/.test(value.trim())) {
          const targetAcc = accounts.find(acc => acc.id === sellerId);
          if (targetAcc && targetAcc.grade === 'SELLER') {
            params.sellerId = sellerId;
            break;
          }
        }

        const candidates = sellers
          .map(acc => {
            const usernameLower = (acc.username || '').toLowerCase();
            const nameLower = (acc.name || '').toLowerCase();

            let priority = Infinity as number;
            let matchedLen = Infinity as number;

            if (nameLower === query) { priority = 2; matchedLen = nameLower.length; }
            else if (usernameLower.startsWith(query)) { priority = 3; matchedLen = usernameLower.length; }
            else if (nameLower.startsWith(query)) { priority = 4; matchedLen = nameLower.length; }
            else if (usernameLower.includes(query)) { priority = 5; matchedLen = usernameLower.length; }
            else if (nameLower.includes(query)) { priority = 6; matchedLen = nameLower.length; }

            return { acc, priority, matchedLen };
          })
          .filter(c => c.priority !== Infinity)
          .sort((a, b) =>
            a.priority - b.priority ||
            a.matchedLen - b.matchedLen ||
            a.acc.id - b.acc.id
          );

        const foundAccount = candidates.length > 0 ? candidates[0].acc : undefined;

        if (foundAccount) {
          params.sellerId = foundAccount.id;
        } else {
          return;
        }
        break;
      }
      case 'mid_single':
        params.mid_single = value.trim();
        break;
      case 'mid_compare':
        params.mid_compare = value.trim();
        break;
    }

    setSearchParams(prev => ({
      page: 1,
      pageSize: prev.pageSize,
      active: 'false',
      ...params,
    }));
  }, [searchField, accounts]);

  // 페이지 변경
  const handlePageChange = useCallback((page: number, pageSize?: number) => {
    setSearchParams(prev => ({
      ...prev,
      page,
      pageSize: pageSize || prev.pageSize
    }));
  }, []); // searchParams.pageSize 의존성 제거

  // 페이지 변경 시 화면을 위로 이동
  useEffect(() => {
    if (searchParams.page) {
      setTimeout(() => {
        topRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [searchParams.page]);

  /* ==============  선택 슬롯 정보 ============== */
  const selectedIdSet = useMemo(() => new Set(selected as number[]), [selected]);
  const slotById = useMemo(() => new Map(slots.map((s) => [s.slotId, s] as const)), [slots]);
  const selectedSlots = useMemo(() => 
    Array.from(selectedIdSet).map((id) => slotById.get(id)).filter((v): v is Slot => !!v),
    [selectedIdSet, slotById]
  );
  const handleSelectChange = useCallback((ids: Key[]) => {
    startTransition(() => {
      setSelected(ids);
    });
  }, []);

  /* ==============  공통 성공 콜백 ============== */
  const handleSuccess = useCallback((msg: string) => {
    message.success(msg);
    setSelected([]);
  }, []);

  const cloneSlot = useCallback((slot?: Slot) => (slot ? { ...slot } : undefined), []);

  /* ---------------- 삭제 ---------------- */
  const onDelete = useCallback(() => {
    if (!me) return;
    try {
      assertAdminOrManager(me);
    } catch (e) {
      message.error((e as Error).message);
      return;
    }

    const ids = Array.from(new Set(selected as number[]));
    const snapshots = ids.map((id) => ({
      id,
      listSlot: cloneSlot(slotById.get(id)),
      defaultSlot: cloneSlot(slotById.get(id)),
    }));

    dispatch(optimisticDeleteSlots({ ids }));
    handleSuccess(`${ids.length}개 슬롯이 삭제되었습니다.`);

    void Promise.all(
      ids.map((id) =>
        dispatch(deleteSlot({ id, me, optimisticApplied: true }))
          .unwrap()
          .then(() => true)
          .catch(() => false),
      ),
    ).then((results) => {
      const failedSnapshots = snapshots.filter((_, index) => !results[index]);
      if (failedSnapshots.length === 0) return;

      dispatch(rollbackSlots(failedSnapshots));
      fetchInactiveSlots();
      const successCount = ids.length - failedSnapshots.length;
      if (successCount > 0) {
        message.warning(`${failedSnapshots.length}개 슬롯 삭제에 실패하여 복구했습니다.`);
      } else {
        message.error('슬롯 삭제에 실패했습니다.');
      }
    });
  }, [me, selected, dispatch, handleSuccess, cloneSlot, slotById, fetchInactiveSlots]);

  /* ---------------- 엑셀 다운로드 ---------------- */
  const [excelDownloading, setExcelDownloading] = useState(false);
  
  const handleExcelDownload = useCallback(async () => {
    if (!me) return;
    
    setExcelDownloading(true);
    
    try {
      let slotsToExport: Slot[];

      if (selected.length > 0) {
        // 체크된 슬롯이 있으면 선택된 것들만 다운로드
        slotsToExport = selectedSlots;
      } else {
        // 선택된 슬롯이 없으면 전체 조회 (active=false)
        const response = await dispatch(fetchAllSlots({ me, active: false })).unwrap();
        slotsToExport = response.slots;
      }

      if (slotsToExport.length === 0) {
        message.warning('다운로드할 슬롯이 없습니다.');
        return;
      }

      // 클라이언트 사이드에서 엑셀 생성
      const { exportSlotsToExcel } = await import('../../Utils/exportSlotsToExcel');
      const isAllDownload = selected.length === 0; // 선택된 슬롯이 없으면 전체 다운로드
      exportSlotsToExcel(slotsToExport, accounts, isAllDownload);
      message.success(`${slotsToExport.length}개 슬롯이 다운로드되었습니다.`);
    } catch (error) {
      message.error('엑셀 다운로드에 실패했습니다.');
    } finally {
      setExcelDownloading(false);
    }
  }, [dispatch, me, accounts, selectedSlots, selected]);

  /* ==============  에러 알림 ============== */
  useEffect(() => { if (error) message.error(error); }, [error]);

  /* ==============  아직 사용자 정보가 없으면 스피너 ============== */
  if (!me) {
    return <Spin style={{ display: 'block', margin: '160px auto', color: '#B03052' }} />;
  }

  /* ==============  JSX  ============== */
  return (
    <div ref={topRef}>
      <Card title="환불-종료 슬롯 관리" bodyStyle={{ padding: 0 }}>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div style={{ padding: '16px 16px 0 16px' }}>
            <SlotOpsOverrideBanner />
          </div>
          <ManagementBar
            /* 선택 & 액션 */
            selected={selected}
            selectedSlots={selectedSlots}
            searchField={searchField}
            onSearchFieldChange={handleSearchFieldChange}
            onDelete={onDelete}
            /* 검색 / 상태 */
            search={search}
            onSearchChange={setSearch}
            onSearch={handleSearch}
            status=""
            onStatusChange={() => {}}
            /* 엑셀 */
            onExcelDownload={handleExcelDownload}
            excelUploading={false}
            excelDownloading={excelDownloading}
          />

          <SlotTable
            data={slots}
            loading={loading}
            selected={selected}
            onSelectChange={handleSelectChange}
            onInlineUpdate={async () => {}} // 비활성 페이지에서는 인라인 수정 불가
            pagination={pagination ? { ...pagination, current_page: searchParams.page || pagination.current_page, page_size: searchParams.pageSize || pagination.page_size } : undefined}
            onPageChange={handlePageChange}
          />
        </Space>
      </Card>
    </div>
  );
};

export default SlotsInactivePage; 